import type { ReactNode } from 'react';
import { useBriefingHistory } from '../../hooks/useBriefingHistory';
import { formatBriefingCalendarDate } from '../../lib/formatting';
import { RegenerateButton } from './RegenerateButton';

/*
 * QuietDayState — the no-activity / "quiet day" briefing screen, shared by BOTH renderers
 * (bug-418): the legacy TeamTodaySurface `empty` branch and the v2 StandardBrief QuietDayShell.
 *
 * Two things the old bare "Quiet day + Sources" shell got wrong:
 *   1. It was a dead end — nothing to look at on a quiet day.
 *   2. It DROPPED the only path to past briefings. `briefing-history` is not a nav item
 *      (it lives in no TopNav / BottomTabBar / Cmd+K); the in-surface "Past briefings"
 *      button is the sole entry point, and this branch was the one place it went missing.
 *
 * So the quiet day now surfaces the user's most recent briefings inline (reusing the exact
 * BriefingHistorySurface row markup — no new styling) plus a "see all" link. `children` is the
 * inline CTA slot in the action row (the Sources button); `footer` is a below-the-fold slot
 * (the legacy path's mobile connect-on-desktop nudge + sheet). The history fetch is scoped to
 * THIS component, so it only fires when the quiet-day screen mounts, never on a ready briefing.
 */

const RECENT_LIMIT = 3;

export function QuietDayState({
  onNav,
  children,
  footer,
  readOnly,
}: {
  onNav: (id: string) => void;
  children?: ReactNode;
  footer?: ReactNode;
  // Historical (read-only) briefings must not offer Regenerate — that mutates TODAY's brief.
  readOnly?: boolean;
}) {
  const { data } = useBriefingHistory();
  const recent = (data?.briefings ?? []).slice(0, RECENT_LIMIT);
  const total = data?.total_count ?? recent.length;

  const pill =
    'rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground';

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1500px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        <div className="border-b border-border pb-3">
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">DELIVER</span>
        </div>
        <section className="py-7">
          <h1 className="font-['Instrument_Serif'] text-[clamp(38px,4.6vw,64px)] font-normal leading-[.95] tracking-[-.02em]">
            Quiet day
          </h1>
          <p className="mt-5 max-w-xl text-[15px] leading-6 text-muted-foreground">
            No team activity in the last 24 hours.
          </p>
        </section>

        {recent.length > 0 && (
          <section className="max-w-2xl rounded-[24px] border border-border bg-card p-5 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-6">
            <div className="mb-4 flex items-end justify-between">
              <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">
                Recent briefings
              </p>
              <span className="rounded-full bg-muted px-2.5 py-1 font-['DM_Mono'] text-[10px] text-muted-foreground">
                {recent.length}
              </span>
            </div>
            <div>
              {recent.map((b) => (
                <button
                  key={b.id}
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-3 border-b border-border py-2.5 text-left transition last:border-0 hover:bg-muted/50"
                  onClick={() => onNav(`briefing-history/${b.id}`)}
                >
                  <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">
                    {formatBriefingCalendarDate(b.briefing_date)}
                  </span>
                  <span className="truncate text-sm">{b.preview ?? 'View briefing'}</span>
                  {!b.viewed_at && (
                    <span className="rounded-full bg-[#ead5c7] px-2 py-0.5 font-['DM_Mono'] text-[9px] text-[#8c5040] dark:bg-muted dark:text-muted-foreground">
                      new
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2.5">
          {!readOnly && <RegenerateButton className={pill} />}
          <button className={pill} onClick={() => onNav('briefing-history')}>
            {total > recent.length ? 'See all past briefings' : 'Past briefings'} →
          </button>
          {children}
        </div>

        {footer}
      </div>
    </div>
  );
}
